import React, { useState } from 'react';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { openNotificationWithIcon } from '@mss/mss-component-library';
import Fetch from '../../../common/util/_fetch';
import Button from '../../../common/Button';
import { apiSubtypeToConfigKey, subtypeToCoreAssetName } from './assetTypeMapping';
import type { ExportAssetType } from '../../util/useAddPermissions';

type Props = {
  selectedAssets: any[];
  onPrevious: () => void;
  onComplete: () => void;
  type: ExportAssetType;
  subtypeKey: string;
};

const BulkDeleteTab: React.FC<Props> = ({
  selectedAssets,
  onPrevious,
  onComplete,
  type,
  subtypeKey,
}) => {
  const { t } = useTranslation(['asset', 'common']);
  const [submitting, setSubmitting] = useState(false);

  const groupAssetsByType = () => {
    const groups: Record<string, string[]> = {};
    for (const asset of selectedAssets) {
      const subtype = asset[subtypeKey] ?? 'unknown';
      const configKey = apiSubtypeToConfigKey(type, subtype);
      const coreAssetName = subtypeToCoreAssetName(configKey);
      if (!groups[coreAssetName]) groups[coreAssetName] = [];
      groups[coreAssetName].push(asset.id);
    }
    return groups;
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      const groups = groupAssetsByType();
      let totalDeleted = 0;

      for (const [coreAssetName, assetIds] of Object.entries(groups)) {
        const response = await Fetch.post(
          `/api/core/asset-type/${coreAssetName}/bulk/delete/`,
          { body: JSON.stringify({ asset_ids: assetIds }) }
        );

        if (response.ok) {
          const data = await response.json();
          totalDeleted += data.deleted;
        } else {
          const errorData = await response.json().catch(() => null);
          const message =
            errorData?.detail ||
            (Array.isArray(errorData)
              ? errorData.join(', ')
              : JSON.stringify(errorData)) ||
            t('An error occurred');
          openNotificationWithIcon(
            notification.error,
            t('Bulk delete failed'),
            message
          );
          return;
        }
      }

      openNotificationWithIcon(
        notification.success,
        t('Bulk delete completed'),
        t('{{count}} asset(s) deleted successfully.', {
          count: totalDeleted,
        })
      );
      onComplete();
    } catch {
      openNotificationWithIcon(
        notification.error,
        t('Bulk delete failed'),
        t('An unexpected error occurred. Please try again.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-2">{t('Delete assets')}</h3>
        <p className="text-gray-500 text-sm">
          {t(
            'By clicking the submit button below, the selected assets will be deleted. Please make sure the correct assets are selected.'
          )}
        </p>
      </div>

      <div className="flex justify-between mt-6">
        <Button onClick={onPrevious}>{t('Previous')}</Button>
        <Button
          variant="primary"
          disabled={submitting}
          loading={submitting}
          onClick={handleDelete}
        >
          {t('Submit')}
        </Button>
      </div>
    </div>
  );
};

export default BulkDeleteTab;
